import { Bundle, Course, Tag, TagCategoryWithTags } from '../types';

/**
 * Grouped catalogue — the shelf carries the taxonomy.
 *
 * A tenant whose courses form a real grid (Revise: Lab × Paper × Question)
 * can opt its catalogue into a grouped shelf view instead of the flat grid:
 * courses group under the tags of one category (the shelves), sub-group under
 * a second (the rows), and a toggle pivots the page so the secondary axis
 * becomes the shelf, scoped to one of its tags. Tenants without the config
 * keep the flat grid untouched.
 *
 * Everything here is pure data-shaping over the tenant's OWN tag vocabulary —
 * nothing is Revise-specific. Configured per tenant in
 * `branding.catalog` (see CatalogBrandingConfig); design + decision record in
 * design-explorations/student-ia-revise/.
 */

/** Shape of `tenant.branding.catalog`. All fields optional — absent = flat grid. */
export interface CatalogBrandingConfig {
  /**
   * Tag categories that SCOPE the catalogue — outermost first. A scope
   * partitions: the student is in exactly one value at a time and everything
   * else on the page is measured inside it, unlike `groupBy`/`pivotBy` (which
   * arrange what's in scope) or the filter bar (which narrows within it).
   *
   * A list rather than a single field even though Revise names one category
   * today, so adding Subject or Grade later is a settings edit rather than a
   * code change. Resolution + the scope predicate live in `lib/catalogScope`.
   */
  scopeBy?: string[];
  /**
   * Tag NAME a student lands on before they have chosen or enrolled in
   * anything (e.g. "CAPS"). Named rather than implied — falling back to "the
   * first tag" would change meaning whenever somebody reorders them.
   */
  scopeDefault?: string;
  /** Tag category whose tags become the top-level shelves (e.g. "Lab"). */
  groupBy?: string;
  /**
   * Tag category that sub-groups each shelf and drives the pivot toggle
   * (e.g. "Paper Number"). Optional: without it, shelves have no sub-rows
   * and no pivot.
   */
  pivotBy?: string;
  /**
   * Shelf-header blurbs keyed by tag name ("Study Lab" → one explanatory
   * sentence). Lives in branding rather than a DB column so the page is
   * correct with it absent; if this outgrows branding, a `tags.description`
   * column is the natural home.
   */
  blurbs?: Record<string, string>;
}

export interface GroupingConfig {
  primary: TagCategoryWithTags;
  secondary: TagCategoryWithTags | null;
  blurbs: Record<string, string>;
}

/**
 * Resolve the branding config against the tenant's live tag categories.
 * Returns null (→ flat grid) when unconfigured, or when the named category
 * doesn't exist / has no tags — a broken config must degrade to the old page,
 * never to an empty one.
 */
export function resolveGroupingConfig(
  branding: CatalogBrandingConfig | undefined | null,
  categories: TagCategoryWithTags[],
): GroupingConfig | null {
  if (!branding?.groupBy) return null;
  const primary = categories.find((c) => c.name === branding.groupBy);
  if (!primary || primary.tags.length === 0) return null;
  const secondary =
    (branding.pivotBy && categories.find((c) => c.name === branding.pivotBy)) || null;
  return {
    primary,
    secondary: secondary && secondary.tags.length > 0 ? secondary : null,
    blurbs: branding.blurbs ?? {},
  };
}

/** One row inside a shelf. `tag` is null for members with no row tag. */
export interface CatalogCell {
  tag: Tag | null;
  courses: Course[];
}

export interface CatalogShelf {
  tag: Tag;
  cells: CatalogCell[];
  /** Total course count across cells (a course in one shelf counts once). */
  total: number;
}

// Named ...Data so it can't collide with the GroupedCatalog component in
// components/catalog/GroupedCatalog.tsx when both are imported.
export interface GroupedCatalogData {
  shelves: CatalogShelf[];
  /**
   * Courses carrying none of the shelf tags. Rendered as a trailing group —
   * a course must never silently vanish because someone forgot to tag it.
   */
  ungrouped: Course[];
}

const hasTag = (course: Course, tagId: string) =>
  (course.tags ?? []).some((t) => t.id === tagId);

/** The course's tag from one of the config's categories (null when untagged). */
export function courseTagIn(
  course: Pick<Course, 'tags'>,
  category: TagCategoryWithTags | null,
): Tag | null {
  if (!category) return null;
  return (course.tags ?? []).find((t) => t.categoryId === category.id) ?? null;
}

const byShelfOrder = (a: Course, b: Course) =>
  (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.title.localeCompare(b.title);

/** Group `courses` into shelves by `shelfTags`, each sub-grouped by `rowTags`. */
function buildShelves(
  courses: Course[],
  shelfTags: Tag[],
  rowTags: Tag[] | null,
): CatalogShelf[] {
  return shelfTags
    .map((shelfTag) => {
      const members = courses.filter((c) => hasTag(c, shelfTag.id)).sort(byShelfOrder);
      let cells: CatalogCell[];
      if (rowTags && rowTags.length > 0) {
        cells = rowTags
          .map((rowTag) => ({
            tag: rowTag,
            courses: members.filter((c) => hasTag(c, rowTag.id)),
          }))
          .filter((cell) => cell.courses.length > 0);
        const rowless = members.filter((c) => !rowTags.some((t) => hasTag(c, t.id)));
        if (rowless.length > 0) cells.push({ tag: null, courses: rowless });
      } else {
        cells = members.length > 0 ? [{ tag: null, courses: members }] : [];
      }
      return { tag: shelfTag, cells, total: members.length };
    })
    .filter((shelf) => shelf.total > 0);
}

/** The default view: primary tags as shelves, secondary tags as rows. */
export function groupCourses(courses: Course[], config: GroupingConfig): GroupedCatalogData {
  const shelves = buildShelves(courses, config.primary.tags, config.secondary?.tags ?? null);
  const ungrouped = courses.filter(
    (c) => !config.primary.tags.some((t) => hasTag(c, t.id)),
  );
  return { shelves, ungrouped };
}

/**
 * The pivoted view: one shelf for the selected secondary tag, with the
 * primary tags as its rows. Courses carrying the pivot tag but none of the
 * primary tags land in a trailing null row — same never-vanish rule.
 */
export function pivotCourses(
  courses: Course[],
  config: GroupingConfig,
  pivotTag: Tag,
): CatalogShelf {
  const scoped = courses.filter((c) => hasTag(c, pivotTag.id));
  const shelf = buildShelves(scoped, [pivotTag], config.primary.tags)[0];
  return shelf ?? { tag: pivotTag, cells: [], total: 0 };
}

/** What a shelf card shows: an optional slot chip ("Q3") + the trimmed title. */
export interface ShelfCardLabel {
  slot: string | null;
  title: string;
}

/**
 * Strip the group context out of a card's title. The shelf and row headers
 * already say "Study Lab" and "Paper 1", so a title written to the tenant's
 * naming convention ("Study Lab: Paper 1 | Q3 | Civil Society Protests")
 * collapses to a slot chip + the topic. Driven entirely by the tag names on
 * screen — a title that doesn't lead with them passes through untouched, so
 * this is a no-op for tenants with free-form titles.
 */
export function shelfCardLabel(title: string, contextTags: Array<Tag | null>): ShelfCardLabel {
  let rest = title;
  let stripped = true;
  const names = contextTags.filter((t): t is Tag => t !== null).map((t) => t.name);
  while (stripped) {
    stripped = false;
    for (const name of names) {
      for (const sep of [': ', ' | ']) {
        const prefix = name + sep;
        if (rest.startsWith(prefix)) {
          rest = rest.slice(prefix.length);
          stripped = true;
        }
      }
    }
  }
  const slotMatch = rest.match(/^(Q\d+)\s*\|\s*/);
  if (slotMatch) {
    return { slot: slotMatch[1], title: rest.slice(slotMatch[0].length).trim() || title };
  }
  return { slot: null, title: rest.trim() || title };
}

/**
 * Paid bundles whose every member is a course carrying `tag` — e.g. "Paper 1,
 * Prepared" for the Paper 1 pivot. Free bundles are excluded everywhere in the
 * grouped view: their members ARE the shelves, so rendering the pack too would
 * put the same product at two altitudes (the duplication the audit called out).
 */
export function bundlesMatchingTag(
  bundles: Bundle[],
  courses: Course[],
  tag: Tag,
): Bundle[] {
  const tagged = new Set(courses.filter((c) => hasTag(c, tag.id)).map((c) => c.id));
  return bundles.filter(
    (b) =>
      b.price > 0 &&
      b.courses.length > 0 &&
      b.courses.every((m) => tagged.has(m.id)),
  );
}

/**
 * Paid bundles that reach beyond `tag` — at least one member inside it and at
 * least one outside (e.g. "Both Papers, Prepared" seen from the Paper 1
 * pivot). Rendered as the pivot's footer upsell.
 */
export function bundlesSpanningTag(
  bundles: Bundle[],
  courses: Course[],
  tag: Tag,
): Bundle[] {
  const tagged = new Set(courses.filter((c) => hasTag(c, tag.id)).map((c) => c.id));
  return bundles.filter((b) => {
    if (b.price <= 0 || b.courses.length === 0) return false;
    const inside = b.courses.filter((m) => tagged.has(m.id)).length;
    return inside > 0 && inside < b.courses.length;
  });
}
